import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export const INSPECTOR_SECTIONS_CLASS =
  "[&>section:not(:first-child)]:border-t [&>section:not(:first-child)]:border-white/10";

export const PANEL_SECTION_CLASS = "py-3";
export const PANEL_SECTION_TITLE_CLASS = "text-[11px] uppercase text-slate-500";
export const PANEL_SECTION_HEADER_BUTTON_CLASS =
  "grid size-6 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100 disabled:opacity-40";

export function PanelSection({
  title,
  headerRight,
  children,
  className,
}: {
  title?: ReactNode;
  headerRight?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const hasHeader = title !== undefined || headerRight !== undefined;

  return (
    <section className={cn(PANEL_SECTION_CLASS, "min-w-0", className)}>
      {hasHeader ? (
        <div className="flex min-w-0 items-center justify-between gap-2">
          {typeof title === "string" ? <div className={PANEL_SECTION_TITLE_CLASS}>{title}</div> : title}
          {headerRight}
        </div>
      ) : null}
      {children ? <div className={cn(hasHeader ? "mt-2" : undefined, "min-w-0")}>{children}</div> : null}
    </section>
  );
}

export function PanelSectionIconButton({
  label,
  onClick,
  children,
  expanded,
  disabled,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  expanded?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      disabled={disabled}
      onClick={onClick}
      className={PANEL_SECTION_HEADER_BUTTON_CLASS}
    >
      {children}
    </button>
  );
}

export function PanelSectionAddButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <PanelSectionIconButton label={label} onClick={onClick} disabled={disabled}>
      <Plus className="size-3.5" aria-hidden="true" />
    </PanelSectionIconButton>
  );
}

export function PanelSectionStyleButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className={PANEL_SECTION_HEADER_BUTTON_CLASS}
      aria-label={label}
      onClick={onClick}
      title={label}
    >
      ◆
    </button>
  );
}

export function PanelSectionHeaderActions({
  styleLabel,
  onOpenStyles,
  addLabel,
  onAdd,
}: {
  styleLabel: string;
  onOpenStyles?: () => void;
  addLabel: string;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {onOpenStyles ? <PanelSectionStyleButton label={styleLabel} onClick={onOpenStyles} /> : null}
      <PanelSectionAddButton label={addLabel} onClick={onAdd} />
    </div>
  );
}
